import localforage from 'localforage';
import { messageStore } from './messages';

const conversationStorage = localforage.createInstance({
  name: 'GoPlausible-Algorand-Agency',
  storeName: 'conversations',
  version: 1.0,
  description: 'Algorand Agency platform conversation storage'
});

export const conversationStore = {
  async getConversations(walletAddress: string) {
    try {
      const keys = await conversationStorage.keys();
      const prefix = `conversation_${walletAddress}_`;
      const conversationKeys = keys.filter(key => key.startsWith(prefix));
      
      console.log(`[Storage] Found ${conversationKeys.length} conversations for wallet ${walletAddress}`);
      
      const conversations = await Promise.all(
        conversationKeys.map(async key => {
          const data = await conversationStorage.getItem(key);
          //console.log(`[Storage] Retrieved conversation data for key ${key}:`, data);
          return data;
        })
      );

      return conversations
        .filter((conv): conv is any => conv !== null)
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    } catch (error) {
      console.error('Error getting conversations from storage:', error);
      return [];
    }
  },

  async getConversationMessages(walletAddress: string, conversationId: string) {
    try {
      const key = `conversation_${walletAddress}_${conversationId}`;
      const conversation = await conversationStorage.getItem<{ messageIds: string[] }>(key);
      
      //console.log(`[Storage] Retrieved conversation data for key ${key}:`, conversation);
      
      if (!conversation?.messageIds) {
        console.log(`[Storage] No message IDs found for conversation ${conversationId}`);
        return [];
      }

      const messages = await messageStore.getMessages(conversation.messageIds);
      //console.log(`[Storage] Retrieved ${messages.length} messages for conversation ${conversationId}`);
      return messages;
    } catch (error) {
      console.error('Error getting conversation messages from storage:', error);
      return [];
    }
  },

  async setConversation(walletAddress: string, conversation: any, messageIds: string[] = []) {
    try {
      const key = `conversation_${walletAddress}_${conversation.id}`;
      const existingConversation = await conversationStorage.getItem<any>(key);
      
      const conversationData = {
        ...conversation,
        messageIds: existingConversation?.messageIds || messageIds,
        // updated_at: new Date().toISOString()
      };

      console.log(`[Storage] Storing conversation with key ${key}:`, conversationData);
      await conversationStorage.setItem(key, conversationData);
    } catch (error) {
      console.error('Error setting conversation in storage:', error);
      throw error;
    }
  },

  async updateConversation(walletAddress: string, conversationId: string, updates: any) {
    try {
      const key = `conversation_${walletAddress}_${conversationId}`;
      const conversation = await conversationStorage.getItem<any>(key);
      
      if (!conversation) {
        console.log(`[Storage] Creating new conversation: ${key}`);
        const newConversation = {
          id: conversationId,
          messageIds: [],
          ...updates,
          // Only set timestamps if they're not provided in updates
          created_at: updates.created_at || new Date().toISOString(),
          updated_at: updates.updated_at || new Date().toISOString()
        };
        await conversationStorage.setItem(key, newConversation);
        return;
      }

      const updatedConversation = {
        ...conversation,
        ...updates,
        // Only update timestamp if not provided in updates
        updated_at: updates.updated_at || new Date().toISOString()
      };

      await conversationStorage.setItem(key, updatedConversation);
    } catch (error) {
      console.error('Error updating conversation in storage:', error);
      throw error;
    }
  },

  async addMessageToConversation(walletAddress: string, conversationId: string, message: any) {
    try {
      const key = `conversation_${walletAddress}_${conversationId}`;
      //console.log(`[Storage] Adding message to conversation ${key}:`, message);

      // First store the message
      await messageStore.setMessage(message.id, message);
      console.log('[Storage] Message stored successfully');

      // Then update the conversation's message list
      const conversation = await conversationStorage.getItem<any>(key);
      
      if (!conversation) {
        console.log(`[Storage] Creating new conversation for message`);
        const newConversation = {
          id: conversationId,
          messageIds: [message.id],
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        await conversationStorage.setItem(key, newConversation);
        return;
      }

      const messageIds = conversation.messageIds || [];
      if (!messageIds.includes(message.id)) {
        messageIds.push(message.id);
      }

      // When adding a message, we do want to update the timestamp
      // This is the expected behavior for message additions
      const updatedConversation = {
        ...conversation,
        messageIds,
        updated_at: new Date().toISOString()
      };

      // console.log(`[Storage] Updating conversation with new message IDs:`, messageIds);
      await conversationStorage.setItem(key, updatedConversation);
      console.log('[Storage] Conversation updated successfully');
    } catch (error) {
      console.error('[Storage] Error in addMessageToConversation:', error);
      throw error;
    }
  },

  async removeConversation(walletAddress: string, conversationId: string) {
    try {
      const key = `conversation_${walletAddress}_${conversationId}`;
      const conversation = await conversationStorage.getItem<{ messageIds: string[] }>(key);
      
      if (conversation?.messageIds) {
        // Remove all messages
        await Promise.all(
          conversation.messageIds.map(id => messageStore.removeMessage(id))
        );
      }

      // Remove conversation
      await conversationStorage.removeItem(key);
    } catch (error) {
      console.error('Error removing conversation from storage:', error);
      throw error;
    }
  },

  async clear() {
    try {
      await conversationStorage.clear();
    } catch (error) {
      console.error('Error clearing conversation storage:', error);
      throw error;
    }
  }
};
